import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Mic,
  Calendar,
  Award,
  ShieldCheck,
  Sparkles,
  Star,
  Quote,
  Play,
  Mail,
  MapPin,
  ExternalLink,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import MaskedReveal from "@/components/motion/MaskedReveal";
import HexMark from "@/components/motion/HexMark";
import Seo, { PERSON_JSONLD, buildBreadcrumbs } from "@/components/Seo";

// ─────────────────────────────────────────────────────────────
// Sir Dion Magee — Speaker · Emcee · Facilitator · Instructor
// Consolidates content that previously lived on dionmagee.com
// into a single, editorial page on Magee Enterprises.
// ─────────────────────────────────────────────────────────────

const HERO_SLIDES = [
  {
    id: "portrait-blue",
    src: "/leadership/dion-magee-portrait.jpg",
    align: "right",
  },
  {
    id: "stage",
    src: "/portfolio/case04-grand-ballroom-screens.jpg",
    align: "center",
  },
];

const BENEFITS = [
  {
    n: "01",
    title: "Set the stage for success.",
    body: "We understand your event purpose and deliver an immersive introduction, announce program components, and captivate the audience with electrifying engagement.",
  },
  {
    n: "02",
    title: "Engage your audience.",
    body: "Sir Dion Magee enthusiastically follows your scripts and cues while ensuring success by providing unwavering support to your production team.",
  },
  {
    n: "03",
    title: "Deliver an amazing experience.",
    body: "A seasoned facilitator, Sir Dion enhances your brand's voice through a combination of professionalism and adept improvisation.",
  },
];

const CREDENTIALS = [
  { year: "2025", code: "ATP-I", label: "Authorized Training Partner Instructor — PMI" },
  { year: "2023", code: "CHE", label: "Certified Hospitality Educator" },
  { year: "2023", code: "CGSP", label: "Certified Guest Service Professional" },
  { year: "2022", code: "CTD", label: "Certified Travel Director" },
  { year: "2022", code: "PCA", label: "Pandemic Compliance Advisor" },
  { year: "2021", code: "CMP", label: "Certified Meeting Professional" },
  { year: "2019", code: "CTA", label: "Certified Travel Advisor" },
  { year: "2018", code: "GRCP", label: "Governance, Risk & Compliance Professional & Auditor" },
  { year: "2018", code: "CSSBB", label: "Certified Six Sigma Black Belt" },
  { year: "2018", code: "PMP", label: "Project Management Professional" },
  { year: "2017", code: "CIS", label: "Certified Incentive Specialist" },
  { year: "2017", code: "HMCC", label: "Healthcare Meeting Compliance Certificate" },
  { year: "2017", code: "CRCMP", label: "Certified Risk & Compliance Management Professional" },
  { year: "2015", code: "CMM", label: "Certificate in Meeting Management" },
];

const TESTIMONIALS = [
  {
    quote:
      "Nothing but perfection! We love having Mr. Magee open our educational session with lots of energy and passion for his work.",
    name: "David Wells, CDE",
    org: "AWP Organization",
  },
  {
    quote:
      "Dion was amazing to work with! It was a sincere pleasure. Thank you for the wonderful professionalism you brought to our partnership.",
    name: "T. Lee",
    org: "CBS Studios",
  },
  {
    quote:
      "Great guy and extremely professional. Dion has an amazing gift and talent to the work he does. Don't settle for anything less.",
    name: "Evelio Orengo Jr.",
    org: "Photographer",
  },
  {
    quote:
      "Wow! What an event! I can't thank you enough for emceeing this year's CMP/CMM event. Your energy is unmatched and authentic. You lit up the room and carried that energy throughout the entire event.",
    name: "Alyssa Lippincott",
    org: "MPI NJ",
  },
  {
    quote:
      "Thank you again for the time you spent speaking to the students and preparing a wonderful presentation. You have given the students a wonderful example of some of the amazing things someone can do in the events world — we all felt inspired by your expertise, energy, and passion.",
    name: "Sarah Agate",
    org: "University of Illinois Urbana-Champaign",
  },
];

const OCCASIONS = [
  "Corporate Meetings",
  "Conferences",
  "Retreats",
  "Weddings",
  "Galas",
  "Training Programs",
  "Graduations",
  "Awards Ceremonies",
];

const PORTFOLIO = [
  {
    tag: "Verizon · Case Study 01",
    title: "Employee Recognition — at national scale.",
    body: "Verizon Excellence Awards — a multi-city incentive program engineered to feel identical in every room, from Manhattan to Honolulu.",
    img: "/portfolio/case01-incentives-verizon.jpg",
    href: "/about#portfolio",
    cta: "View case study",
  },
  {
    tag: "BioTech · Case Study 02",
    title: "BioTech Investigator Meetings — three cities, nine months.",
    body: "A CMP-led investigator meeting program orchestrated across Paris, Sydney &amp; Chicago — precise, compliant, and repeatable across continents.",
    img: "/portfolio/hero_paris.jpg",
    href: "/about#case02",
    cta: "View case study",
  },
];

const PARTNERS = [
  { label: "TEDx", sub: "Speaker · Alumnus" },
  { label: "PMI", sub: "Project Management Institute" },
  { label: "MPI", sub: "Meeting Professionals International" },
];

const BOOKING_OPTIONS = [
  {
    title: "Business / General Consultation",
    duration: "15 minutes",
    price: "Complimentary",
    href: "https://tidycal.com/sirdionmagee/business-general-consultation-15-minutes",
    body: "Schedule a virtual meeting with a business advisor. Let's discover new projects together.",
  },
  {
    title: "OmniPear · 15-Minute Meeting",
    duration: "15 minutes",
    price: "Complimentary",
    href: "https://tidycal.com/sirdionmagee/omnipear-15-minute-meeting",
    body: "A complimentary session for OmniPear's courses and programs, with a certified academic instructor.",
  },
  {
    title: "Instructor 1:1 Coaching · Express",
    duration: "30 minutes",
    price: "Complimentary",
    href: "https://tidycal.com/sirdionmagee/instructorsession-1r8pkx2",
    body: "Instructor guidance and coaching for personalized direction.",
  },
  {
    title: "Instructor 1:1 Coaching · Pre-Pay",
    duration: "45 minutes",
    price: "$50",
    href: "https://tidycal.com/sirdionmagee/instructorsession",
    body: "Extended instructor guidance with additional coaching and prep support.",
  },
];

// ═══════════════════════════════════════════════════════
// PortraitTilt3D — Interactive tilt on the Sir Dion portrait
// ═══════════════════════════════════════════════════════
function PortraitTilt3D({ children }) {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: -py * 10, y: px * 14 });
  };
  const onLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid="speaker-portrait-tilt"
      style={{ perspective: "1400px" }}
    >
      <div
        className="rounded-3xl transition-transform duration-200 ease-out will-change-transform"
        style={{
          transformStyle: "preserve-3d",
          transform: `rotateY(${tilt.y}deg) rotateX(${tilt.x}deg)`,
          filter:
            "drop-shadow(0 30px 45px rgba(6,102,182,0.28)) drop-shadow(0 12px 22px rgba(6,182,212,0.18))",
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function Speaker() {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6500);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="bg-[var(--bg)] text-[var(--text)]">
      <Seo
        title="Sir Dion Magee — Emcee · Facilitator · Certified Instructor"
        description="Sir Dion Magee, CHE, CMP, PMP, CMM, CGSP, GRCP, GRCA, CSSBB, CRCMP, HMCC, S CIS, CTD, CTA — award-winning meeting, event & wedding lifestyle expert, adjunct professor at Temple University, and founder of Magee Enterprises. 25+ years of engaging emcee, keynote, coaching, and training experience across corporate meetings, hospitality, life sciences, and education."
        path="/speaker"
        image="/portfolio/case04-grand-ballroom-screens.jpg"
        keywords="Sir Dion Magee, Dion Magee, emcee, host, facilitator, keynote speaker, PMP instructor, CMP, CHE, meeting compliance, hospitality educator, Clincierge, OmniPear, Temple University, Magee Enterprises"
        jsonLd={[
          PERSON_JSONLD,
          buildBreadcrumbs([
            ["Home", "/"],
            ["Speaker", "/speaker"],
          ]),
        ]}
      />

      {/* ════════ HERO SLIDER ════════ */}
      <section className="relative pt-28 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-30 pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="relative rounded-[28px] md:rounded-[36px] overflow-hidden bg-[#050A14] min-h-[560px] md:min-h-[720px]">
            {/* Backgrounds */}
            <AnimatePresence>
              <motion.div
                key={HERO_SLIDES[slide].id}
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.03 }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0"
              >
                <img
                  src={HERO_SLIDES[slide].src}
                  alt="Sir Dion Magee"
                  className={`w-full h-full object-cover ${HERO_SLIDES[slide].align === "right" ? "object-right" : "object-center"}`}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#050A14]/85 via-[#050A14]/55 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050A14]/80 via-transparent to-transparent" />
              </motion.div>
            </AnimatePresence>

            {/* Content */}
            <div className="relative z-10 h-full min-h-[560px] md:min-h-[720px] flex flex-col justify-end p-6 md:p-12 lg:p-16">
              <div className="max-w-3xl text-white">
                <div className="flex items-center gap-4 mb-6 flex-wrap">
                  <span className="w-10 h-px bg-white/30" />
                  <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-cyan-300">
                    Speaker · Emcee · Facilitator
                  </span>
                </div>
                <MaskedReveal
                  lines={["Sir Dion", "Magee."]}
                  className="font-editorial tracking-[-0.03em] leading-[0.94] text-[14vw] md:text-[8.4vw] lg:text-[7vw] text-white"
                  stagger={0.09}
                />
                <Reveal delay={0.25}>
                  <p className="mt-5 text-xl md:text-2xl leading-tight text-white/85 max-w-2xl">
                    An engaging, interactive &amp; professional emcee &amp; host.{" "}
                    <span className="italic text-cyan-300">Bring your events &amp; meetings to life.</span>
                  </p>
                </Reveal>
                <Reveal delay={0.4}>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <a
                      href="https://tidycal.com/sirdionmagee/business-general-consultation-30-minutes"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary"
                      data-testid="speaker-hero-cta-book"
                    >
                      Book a chat <Calendar size={14} />
                    </a>
                    <a href="#booking" className="btn-dark backdrop-blur" data-testid="speaker-hero-cta-scroll">
                      See booking options <ArrowRight size={14} />
                    </a>
                  </div>
                </Reveal>

                {/* Meta strip */}
                <div className="mt-10 pt-6 border-t border-white/15 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { v: "25", l: "Years · Industry Experience" },
                    { v: "PMP", l: "ATP-I Instructor · PMI" },
                    { v: "CHE", l: "Certified Hospitality Educator" },
                    { v: "CMP", l: "Certified Meeting Professional" },
                  ].map((m) => (
                    <div key={m.l}>
                      <div className="font-editorial text-3xl md:text-4xl text-white leading-none">{m.v}</div>
                      <div className="mt-2 font-mono-brand text-[9.5px] uppercase tracking-[0.24em] text-white/60">
                        {m.l}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Slide indicators */}
              <div className="absolute top-6 right-6 flex gap-2">
                {HERO_SLIDES.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => setSlide(i)}
                    aria-label={`Slide ${i + 1}`}
                    data-testid={`speaker-hero-dot-${i}`}
                    className={`w-8 h-1 rounded-full transition-all ${
                      i === slide ? "bg-cyan-300 w-12" : "bg-white/30 hover:bg-white/50"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Occasions strip */}
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[13px] text-[var(--text-muted)]">
              <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text)]">
                Formats
              </span>
              {OCCASIONS.map((o, i) => (
                <React.Fragment key={o}>
                  <span>{o}</span>
                  {i < OCCASIONS.length - 1 && <span className="text-[var(--text-dim)]">·</span>}
                </React.Fragment>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ════════ BIO / INTRO ════════ */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 hex-pattern-dense opacity-30 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[420px] h-[420px] -top-24 -right-24 opacity-30" />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-14 items-start">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <span className="chip">
                <Mic size={12} /> Great Facilitator · Coach · Instructor
              </span>
              <h2 className="mt-6 font-editorial text-3xl md:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.03em]">
                Engaging techniques to facilitate your key message,{" "}
                <span className="italic text-cyan-600 font-medium">
                  corporate direction, and unforgettable attendee experiences.
                </span>
              </h2>
              <div className="mt-8 space-y-5 text-[var(--text)]/80 text-[15.5px] leading-relaxed max-w-2xl">
                <p>
                  Sir Dion Magee was honored for his leadership at OmniPear&trade;, a leader in innovative professional development &amp; education. The team won a Brandon Hall Group&trade; Bronze award for excellence in the &ldquo;Best Certification Program&rdquo; category — recognizing best practices across Learning &amp; Development, Talent Management, Leadership Development, and the Future of Work.
                </p>
                <p>
                  Twenty-five years across the events, hospitality, and education industries. Fourteen certifications in project management, meeting management, hospitality, risk, and compliance. One consistent standard: make the room feel like it&rsquo;s exactly where it&rsquo;s supposed to be.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="https://www.credly.com/badges/e6b203e2-5776-422b-b94b-292302186b6f"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-dark"
                  data-testid="speaker-credly"
                >
                  Verify on Credly <ExternalLink size={12} />
                </a>
                <Link to="/contact" className="btn-primary" data-testid="speaker-inline-cta">
                  Request Sir Dion <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <Reveal delay={0.2}>
              <PortraitTilt3D>
                <div className="relative rounded-3xl overflow-hidden border border-[var(--border)]">
                  <img
                    src="/portfolio/case04-grand-ballroom-screens.jpg"
                    alt="Sir Dion Magee on stage"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </PortraitTilt3D>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <a
                  href="https://www.credly.com/badges/e6b203e2-5776-422b-b94b-292302186b6f"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-2xl border border-[var(--border)] bg-white p-4 flex items-center gap-3 hover:border-[var(--accent)]/40 transition-colors"
                >
                  <span className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-sky-600 text-white flex items-center justify-center shadow-[0_8px_20px_-8px_rgba(6,182,212,0.5)]">
                    <Award size={22} strokeWidth={2} />
                  </span>
                  <div className="min-w-0">
                    <div className="font-mono-brand text-[9.5px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Credly
                    </div>
                    <div className="text-[12.5px] leading-tight">PMP · ATP-I Instructor</div>
                  </div>
                </a>
                <div className="rounded-2xl border border-[var(--border)] bg-white p-4 flex items-center gap-3">
                  <span className="shrink-0 w-12 h-12 rounded-xl bg-[#0A0A0D] text-white flex items-center justify-center">
                    <Mic size={22} strokeWidth={2} />
                  </span>
                  <div className="min-w-0">
                    <div className="font-mono-brand text-[9.5px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Speaker
                    </div>
                    <div className="text-[12.5px] leading-tight">TEDx alumnus</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ════════ FULL BIOGRAPHY (SEO-rich long-form) ════════ */}
      <section
        id="biography"
        aria-labelledby="biography-heading"
        className="relative py-20 md:py-28 overflow-hidden bg-white border-t border-[var(--border)]"
      >
        <div className="absolute inset-0 hex-pattern opacity-25 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[420px] h-[420px] -top-24 -left-24 opacity-25" />

        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex items-center gap-4 mb-6">
              <span className="w-10 h-px bg-[var(--text)]/40" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--text-muted)]">
                Full biography · About Sir Dion
              </span>
            </div>
            <h2
              id="biography-heading"
              className="font-editorial text-3xl md:text-5xl leading-[1.02] tracking-[-0.02em] max-w-3xl"
            >
              About{" "}
              <span className="italic text-cyan-600 font-medium">Sir Dion Magee.</span>
            </h2>
          </Reveal>

          <div
            data-testid="speaker-biography-body"
            className="mt-10 md:mt-14 prose prose-lg max-w-none text-[var(--text)]/85 leading-relaxed space-y-6 text-[16.5px]"
          >
            <Reveal>
              <p>
                <strong className="text-[var(--text)]">Sir Dion Magee, CHE, CMP, PMP, CMM, CGSP, GRCP, GRCA, CSSBB, CRCMP, HMCC, S CIS, CTD, CTA</strong>{" "}
                — hailed as an artistic visionary whose combined skill, creative talent, and outstanding
                ability have made him a pioneer in the meeting and events, hospitality, tourism, and
                education industries.
              </p>
              <p>
                <strong className="text-[var(--text)]">Sir Dion Magee</strong>, award&#8209;winning meeting,
                event &amp; wedding lifestyle expert and project management professional, is a leading
                corporate and social events / wedding industry expert.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <h3 className="!mt-12 font-editorial text-2xl md:text-3xl text-[var(--text)] tracking-[-0.01em]">
                Meetings &amp; Events
              </h3>
              <p>
                As the leader for <strong>Magee Enterprises</strong>, a full-service event &amp; meeting
                management concierge and EventTech solutions agency, Sir Dion is dedicated to following
                his passion for event &amp; meeting excellence.
              </p>
              <p>
                Equally impressive, his team at <strong>Magee Enterprises, LLC</strong> offers a diverse
                group of consulting services and well-executed initiatives for meetings and events.
              </p>
              <p>
                Before transitioning to Magee Enterprises, LLC, Dion began his career as a Meeting Manager
                while being promoted to the Meeting Compliance Officer of Meetings and Events at Verizon.
                His work has transformed corporate events and conference programs (over 14&nbsp;years)
                into breathtaking main attractions and successful promotions.
              </p>
              <p>
                He designed and implemented one of the country&rsquo;s largest and award&#8209;winning
                recognition / incentive programs for the leading global, promotional
                telecommunications, and electronic retail sales company worldwide.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <h3 className="!mt-12 font-editorial text-2xl md:text-3xl text-[var(--text)] tracking-[-0.01em]">
                Industry-Leading Educator
              </h3>
              <p>
                As the owner of <strong>OMNIPEAR</strong> and serving as an Adjunct Professor for{" "}
                <strong>Temple University</strong>, Dion is passionate about teaching hospitality
                professionals to be successful in the hospitality and tourism industries.
              </p>
              <p>
                With the recent launch of OMNIPEAR — an on-line professional development and
                educational platform — he delivers the best hospitality, professional training, and
                prep&#8209;courses for certifications and entrepreneur coaching.
              </p>
            </Reveal>

            <Reveal delay={0.2}>
              <h3 className="!mt-12 font-editorial text-2xl md:text-3xl text-[var(--text)] tracking-[-0.01em]">
                Life Sciences
              </h3>
              <p>
                As a founding father of <strong>Clincierge</strong>, Magee helped transform and improve
                the quality of patient care and services by pioneering this &ldquo;first-concept&rdquo; to
                market in 2013. His contributions have built Clincierge, a well-known and prestigious
                company valued at <strong>$50M</strong>. Clincierge orchestrates patient logistics to
                improve the patient experience, increasing clinical trial recruitment, participation, and
                retention.
              </p>
            </Reveal>

            <Reveal delay={0.25}>
              <blockquote className="!mt-12 border-l-2 border-cyan-500 pl-6 italic text-[var(--text)]/75 text-lg leading-relaxed">
                In recognition of his contribution to project management, Dion was named{" "}
                <strong className="not-italic text-[var(--text)]">
                  Project Management Institute — New Jersey&rsquo;s 2020 Project Manager of the Year
                </strong>
                .
              </blockquote>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ════════ BENEFITS ════════ */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-[var(--surface)]">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="mb-14 md:mb-20">
              <span className="chip">
                <Sparkles size={12} /> Benefits
              </span>
              <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                Get ready to infuse{" "}
                <span className="italic text-cyan-600 font-medium">excitement</span> into your event.
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.n} delay={i * 0.08}>
                <div className="card-white p-6 md:p-8 h-full relative hover:border-[var(--accent)]/40 transition-all duration-500">
                  <span className="corner-cross" style={{ top: 12, right: 12 }} />
                  <div className="numeral-mono text-4xl md:text-5xl select-none mb-6">/{b.n}</div>
                  <h3 className="font-editorial text-xl md:text-2xl leading-tight">{b.title}</h3>
                  <p className="mt-3 text-[var(--text-muted)] text-[14.5px] leading-relaxed">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ PORTFOLIO / AWARDS ════════ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <Award size={12} /> Highlights
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[0.98] tracking-[-0.03em] max-w-2xl">
                  Award-winning &amp;{" "}
                  <span className="italic text-cyan-600 font-medium">audience-tested.</span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                Two recent projects that captured the sound of the room.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {PORTFOLIO.map((p, i) => {
              const CardInner = (
                <article
                  className={`group relative rounded-2xl overflow-hidden border border-[var(--border)] bg-white hover:border-[var(--accent)]/40 hover:shadow-[0_18px_44px_-24px_rgba(6,182,212,0.4)] transition-all duration-500 h-full ${p.href ? "cursor-pointer" : ""}`}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[var(--surface)]">
                    <img
                      src={p.img}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-1000"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <span className="chip">{p.tag}</span>
                    <h3 className="mt-4 font-editorial text-xl md:text-2xl leading-tight">{p.title}</h3>
                    <p
                      className="mt-2 text-[var(--text-muted)] text-[14px] leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: p.body }}
                    />
                    {p.cta && (
                      <span
                        className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-cyan-600 group-hover:gap-2.5 transition-all"
                        data-testid={`speaker-portfolio-cta-${i}`}
                      >
                        {p.cta} <ArrowRight size={13} />
                      </span>
                    )}
                  </div>
                </article>
              );
              return (
                <Reveal key={p.title} delay={i * 0.08}>
                  {p.href ? (
                    <Link
                      to={p.href}
                      aria-label={`${p.title} — view case study`}
                      data-testid={`speaker-portfolio-link-${i}`}
                      className="block h-full"
                    >
                      {CardInner}
                    </Link>
                  ) : (
                    CardInner
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* MID-PAGE BOOK-DION BANNER */}
      <section className="relative py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden card-dark px-6 md:px-12 py-10 md:py-12 group">
              <div className="absolute inset-0 grid-pattern-dark opacity-40 pointer-events-none" />
              <div className="absolute -top-16 -right-20 w-[360px] h-[360px] rounded-full bg-cyan-500/25 blur-3xl" />

              <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-start gap-4">
                  <span className="shrink-0 w-11 h-11 rounded-full bg-cyan-500/15 border border-cyan-300/30 flex items-center justify-center text-cyan-300">
                    <Mic size={18} />
                  </span>
                  <div>
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/85">
                      Have a stage that needs a spark?
                    </p>
                    <h3 className="mt-2 font-editorial text-2xl md:text-3xl leading-tight text-white text-balance max-w-2xl">
                      Book 15 minutes with{" "}
                      <span className="italic font-medium bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">
                        Sir Dion.
                      </span>
                    </h3>
                    <p className="mt-2 text-white/60 text-[14.5px] leading-relaxed max-w-xl">
                      Keynotes, emcee, panels, or a bespoke session — we&rsquo;ll match the format to your audience and outcome.
                    </p>
                  </div>
                </div>
                <Link
                  to="/contact#book"
                  data-testid="speaker-mid-book-cta"
                  className="btn-primary shrink-0 w-full md:w-auto justify-center group-hover:translate-x-0.5 transition-transform"
                >
                  Book Dion <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ════════ CREDENTIALS TIMELINE ════════ */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-[#050A14] text-white">
        <div className="absolute inset-0 grid-pattern-dark opacity-30 pointer-events-none" />
        <div className="absolute -top-32 -left-32 w-[440px] h-[440px] rounded-full bg-cyan-500/15 blur-3xl" />
        <div className="absolute -bottom-24 -right-16 w-[500px] h-[500px] rounded-full bg-sky-500/15 blur-3xl" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="mb-14 md:mb-20">
              <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                <ShieldCheck size={12} /> Industry Experience · 14 Credentials
              </span>
              <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                A decade of{" "}
                <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-cyan-400 bg-clip-text text-transparent italic font-medium">
                  earned certifications.
                </span>
              </h2>
            </div>
          </Reveal>

          <div className="relative">
            {/* Vertical rail */}
            <div
              aria-hidden
              className="absolute left-[7px] sm:left-[15px] top-2 bottom-2 w-px bg-gradient-to-b from-cyan-400/50 via-cyan-500/25 to-transparent"
            />
            <div className="pl-6 sm:pl-10 space-y-6 md:space-y-8">
              {CREDENTIALS.map((c, i) => (
                <Reveal key={`${c.year}-${c.code}`} delay={i * 0.03}>
                  <article className="relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
                    <span
                      aria-hidden
                      className="absolute -left-6 sm:-left-10 top-3 w-3 h-3 rounded-full bg-gradient-to-br from-cyan-400 to-sky-600 shadow-lg"
                      style={{ transform: "translateX(50%)" }}
                    />
                    <div className="w-24 shrink-0">
                      <div className="font-editorial text-3xl md:text-4xl text-white leading-none">
                        {c.year}
                      </div>
                    </div>
                    <div className="flex-1 border-b border-white/10 pb-4 sm:pb-6 flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-6">
                      <span className="font-mono-brand text-[11px] uppercase tracking-[0.22em] text-cyan-300 border border-cyan-300/25 rounded-full px-2.5 py-1 bg-cyan-500/5 w-fit">
                        {c.code}
                      </span>
                      <div className="text-white/80 text-[14.5px] leading-tight">{c.label}</div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════ TESTIMONIALS ════════ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[480px] h-[480px] -top-32 -right-24 opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="mb-14 md:mb-20 max-w-3xl">
              <span className="chip">
                <Star size={12} /> Testimonials
              </span>
              <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[0.98] tracking-[-0.03em]">
                A fresh &amp; engaging{" "}
                <span className="italic text-cyan-600 font-medium">experience.</span>
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.06}>
                <blockquote className="card-white h-full p-6 md:p-8 relative">
                  <Quote size={22} className="text-cyan-500/60" />
                  <p className="mt-4 text-[14.5px] leading-relaxed text-[var(--text)]/85 italic">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <footer className="mt-6 pt-4 border-t border-[var(--border)]">
                    <div className="font-editorial text-[15.5px] leading-tight">{t.name}</div>
                    <div className="mt-1 font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                      {t.org}
                    </div>
                  </footer>
                </blockquote>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ PARTNERS ════════ */}
      <section className="relative py-16 md:py-20 overflow-hidden bg-[var(--surface)]">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center mb-10">
              <span className="chip">
                <Sparkles size={12} /> Partners &amp; Clients
              </span>
              <p className="mt-4 text-[var(--text-muted)] text-sm">
                Thank you for your kind support and partnerships.
              </p>
            </div>
          </Reveal>
          <div className="flex items-center justify-center gap-6 md:gap-8 flex-wrap">
            {PARTNERS.map((p) => (
              <div
                key={p.label}
                className="flex flex-col items-center rounded-2xl border border-[var(--border)] bg-white px-6 py-4 min-w-[140px] hover:border-[var(--accent)]/40 transition-colors"
              >
                <span className="font-editorial text-2xl md:text-3xl tracking-[-0.02em]">
                  {p.label}
                </span>
                <span className="mt-1 font-mono-brand text-[9.5px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                  {p.sub}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ BOOKING ════════ */}
      <section id="booking" className="relative py-20 md:py-28 overflow-hidden scroll-mt-28">
        <div className="absolute inset-0 hex-pattern-dense opacity-30 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[480px] h-[480px] -top-32 -left-32 opacity-30" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <Calendar size={12} /> Booking
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[0.98] tracking-[-0.03em] max-w-2xl">
                  Grab a coffee and{" "}
                  <span className="italic text-cyan-600 font-medium">have a chat.</span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                Reserve a virtual session — for events, coaching, or general business advisory. Powered by TidyCal.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {BOOKING_OPTIONS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.05}>
                <a
                  href={b.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`speaker-booking-${i}`}
                  className="group relative block bg-white border border-[var(--border)] rounded-2xl p-6 h-full hover:border-[var(--accent)]/45 hover:shadow-[0_16px_40px_-18px_rgba(6,182,212,0.4)] hover:-translate-y-0.5 transition-all duration-500"
                >
                  <span className="corner-cross" style={{ top: 10, right: 10 }} />
                  <div className="icon-tile mb-5">
                    <Calendar size={18} />
                  </div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      /{String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-mono-brand text-[9.5px] uppercase tracking-[0.22em] text-cyan-700">
                      {b.duration} · {b.price}
                    </span>
                  </div>
                  <h3 className="font-editorial text-lg md:text-xl leading-tight">{b.title}</h3>
                  <p className="mt-3 text-[13px] text-[var(--text-muted)] leading-relaxed">{b.body}</p>
                  <span className="mt-5 inline-flex items-center gap-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] group-hover:translate-x-0.5 transition-transform">
                    Reserve <ArrowUpRight size={12} />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ CONTACT / CTA ════════ */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden p-8 md:p-16">
            <div className="absolute inset-0 grid-pattern-dark opacity-30" />
            <div className="absolute -top-24 -left-20 w-[380px] h-[380px] rounded-full bg-cyan-500/20 blur-3xl" />
            <div className="absolute -bottom-24 -right-20 w-[420px] h-[420px] rounded-full bg-sky-500/20 blur-3xl" />
            <div className="relative grid grid-cols-12 gap-8 items-end">
              <div className="col-span-12 lg:col-span-8 text-white">
                <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                  <Mic size={12} /> Accepting appearance dates · 2026
                </span>
                <h3 className="mt-6 font-editorial text-3xl md:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.03em]">
                  Ready to book Sir Dion for your{" "}
                  <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-cyan-400 bg-clip-text text-transparent italic font-medium">
                    next room?
                  </span>
                </h3>
                <div className="mt-6 flex flex-col sm:flex-row gap-6 text-white/70">
                  <div className="flex items-center gap-3">
                    <MapPin size={16} className="text-cyan-300" />
                    <span className="text-[14.5px]">New York, New York · USA</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={16} className="text-cyan-300" />
                    <a
                      href="mailto:dion@mageeenterprises.com"
                      className="text-[14.5px] hover:text-white transition-colors"
                    >
                      dion@mageeenterprises.com
                    </a>
                  </div>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-4 flex flex-col sm:flex-row lg:justify-end gap-3">
                <Link
                  to="/contact"
                  className="btn-primary justify-center sm:justify-start w-full sm:w-auto"
                  data-testid="speaker-footer-cta-quote"
                >
                  Request Sir Dion <ArrowRight size={14} />
                </Link>
                <a
                  href="https://tidycal.com/sirdionmagee/business-general-consultation-30-minutes"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-dark backdrop-blur justify-center sm:justify-start w-full sm:w-auto"
                  data-testid="speaker-footer-cta-book"
                >
                  Book a chat <ArrowUpRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ TRADEMARK FOOTNOTE ════════ */}
      <section className="pb-16">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
            &copy; {new Date().getFullYear()} Magee Enterprises, LLC. &ldquo;Sir Dion Magee&rdquo; and &ldquo;Event &amp; Wedding Lifestyle Expert&rdquo; are registered trademarks of Magee Enterprises, LLC.
          </p>
        </div>
      </section>
    </div>
  );
}
